import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings, Users, KeyRound, User, ChevronRight, Download, Trash2, AlertTriangle, Loader2, CheckCircle, Clock } from 'lucide-react';
import { motion } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTeams } from '@/hooks/useTeams';
import { useAccountGDPR } from '@/hooks/useAccountGDPR';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 25,
    },
  },
};

interface SettingsCardProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

function SettingsCard({ to, icon, title, description }: SettingsCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div variants={itemVariants}>
      <Link to={to} className="block">
        <motion.div
          whileHover={{ scale: 1.01, y: -2 }}
          whileTap={{ scale: 0.99 }}
          onHoverStart={() => setIsHovered(true)}
          onHoverEnd={() => setIsHovered(false)}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        >
          <Card className="glass border-border/50 cursor-pointer hover:shadow-lg hover:shadow-primary/5 transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary"
                  >
                    {icon}
                  </motion.div>
                  <div>
                    <CardTitle className="text-base font-medium">{title}</CardTitle>
                    <CardDescription className="mt-1">{description}</CardDescription>
                  </div>
                </div>
                <motion.div
                  animate={{ x: isHovered ? 4 : 0 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </motion.div>
              </div>
            </CardHeader>
          </Card>
        </motion.div>
      </Link>
    </motion.div>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { teams } = useTeams();
  const { signOut, user } = useAuth();
  const primaryTeam = teams[0];

  // GDPR hooks
  const {
    isDevMode,
    exportStatus,
    isLoadingExportStatus,
    requestExport,
    isRequestingExport,
    downloadExport,
    deleteAccount,
    isDeleting,
  } = useAccountGDPR();

  // Dialog states
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleRequestExport = async () => {
    try {
      await requestExport();
      toast.success('Data export requested', {
        description: 'You will receive an email when your data is ready to download.',
      });
    } catch {
      toast.error('Failed to request data export', {
        description: 'Please try again later or contact support.',
      });
    }
  };

  const handleDownloadExport = async () => {
    setIsDownloading(true);
    try {
      await downloadExport();
      toast.success('Download started');
      setShowExportDialog(false);
    } catch {
      toast.error('Failed to download export', {
        description: 'Please try again later.',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      await deleteAccount(deleteConfirmation);
      toast.success('Account deleted');
      await signOut();
      navigate('/');
    } catch {
      toast.error('Failed to delete account', {
        description: 'Please try again later or contact support.',
      });
    }
  };

  const getExportStatusDisplay = () => {
    if (isLoadingExportStatus) {
      return { icon: <Loader2 className="h-4 w-4 animate-spin" />, text: 'Checking status...' };
    }
    switch (exportStatus?.status) {
      case 'pending':
        return { icon: <Clock className="h-4 w-4 text-yellow-500" />, text: 'Export in progress...' };
      case 'ready':
        return { icon: <CheckCircle className="h-4 w-4 text-green-500" />, text: 'Export ready to download' };
      case 'expired':
        return { icon: <AlertTriangle className="h-4 w-4 text-orange-500" />, text: 'Export expired' };
      default:
        return { icon: <Download className="h-4 w-4" />, text: 'Request a copy of your data' };
    }
  };

  const exportStatusDisplay = getExportStatusDisplay();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="max-w-4xl mx-auto"
    >
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center gap-3 mb-8"
      >
        <motion.div
          whileHover={{ scale: 1.1, rotate: 10 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10"
        >
          <Settings className="h-6 w-6 text-primary" />
        </motion.div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-sm text-muted-foreground">Manage your account and preferences</p>
        </div>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-3"
      >
        <motion.div variants={itemVariants}>
          <Card className="glass border-border/50">
            <CardHeader>
              <div className="flex items-center gap-4">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                  className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted/50 text-muted-foreground"
                >
                  <User className="h-5 w-5" />
                </motion.div>
                <div>
                  <CardTitle className="text-base font-medium">Profile</CardTitle>
                  <CardDescription className="mt-1">
                    Manage your account information and preferences
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground pl-14">
                Profile settings coming soon. You can manage your account through Clerk.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {primaryTeam && (
          <SettingsCard
            to={`/projects/${primaryTeam.id}/team`}
            icon={<Users className="h-5 w-5" />}
            title="Team Management"
            description={`Invite members and manage permissions for ${primaryTeam.name}`}
          />
        )}

        <SettingsCard
          to="/tokens"
          icon={<KeyRound className="h-5 w-5" />}
          title="API Tokens"
          description="Generate and manage service tokens for CLI and CI/CD access"
        />

        {/* Privacy & Data Section */}
        <motion.div variants={itemVariants} className="pt-6">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-1">
            Privacy & Data
          </h2>
        </motion.div>

        {/* Dev Mode Notice */}
        {isDevMode && (
          <motion.div variants={itemVariants}>
            <Card className="glass border-yellow-500/30 bg-yellow-500/5">
              <CardContent className="py-3">
                <p className="text-sm text-yellow-600 dark:text-yellow-500">
                  <strong>Dev Mode:</strong> Data export and account deletion require real user authentication and are disabled in development mode.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Data Export */}
        <motion.div variants={itemVariants}>
          <Card className={`glass border-border/50 ${isDevMode ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary"
                  >
                    <Download className="h-5 w-5" />
                  </motion.div>
                  <div>
                    <CardTitle className="text-base font-medium">Export Your Data</CardTitle>
                    <CardDescription className="mt-1 flex items-center gap-2">
                      {exportStatusDisplay.icon}
                      <span>{exportStatusDisplay.text}</span>
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowExportDialog(true)}
                  disabled={isDevMode}
                >
                  {exportStatus?.status === 'ready' ? 'Download' : 'Export'}
                </Button>
              </div>
            </CardHeader>
          </Card>
        </motion.div>

        {/* Danger Zone */}
        <motion.div variants={itemVariants} className="pt-6">
          <h2 className="text-sm font-semibold text-destructive uppercase tracking-wider mb-3 px-1">
            Danger Zone
          </h2>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className={`glass border-destructive/30 ${isDevMode ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-destructive/10 text-destructive"
                  >
                    <Trash2 className="h-5 w-5" />
                  </motion.div>
                  <div>
                    <CardTitle className="text-base font-medium text-destructive">Delete Account</CardTitle>
                    <CardDescription className="mt-1">
                      Permanently delete your account and all associated data
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={isDevMode}
                >
                  Delete
                </Button>
              </div>
            </CardHeader>
          </Card>
        </motion.div>
      </motion.div>

      {/* Data Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Your Data</DialogTitle>
            <DialogDescription>
              Download a copy of all your data stored in KeyEnv, including projects, secrets metadata,
              team information, and audit logs.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground space-y-2">
              <p><strong className="text-foreground">What&apos;s included:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Account information</li>
                <li>Projects and environments</li>
                <li>Secret names and metadata (values are encrypted)</li>
                <li>Team memberships</li>
                <li>Audit logs</li>
                <li>API token metadata</li>
              </ul>
              <p className="mt-3 text-xs">
                Note: Secret values are exported in encrypted form for security.
              </p>
            </div>
            {exportStatus?.status === 'ready' && exportStatus.expires_at && (
              <div className="mt-4 flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span>Export ready. Expires {new Date(exportStatus.expires_at).toLocaleDateString()}</span>
              </div>
            )}
            {exportStatus?.status === 'pending' && (
              <div className="mt-4 flex items-center gap-2 text-sm text-yellow-600">
                <Clock className="h-4 w-4" />
                <span>Export is being prepared. You&apos;ll receive an email when ready.</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            {exportStatus?.status === 'ready' ? (
              <Button onClick={handleDownloadExport} disabled={isDownloading}>
                {isDownloading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download Export
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={handleRequestExport} disabled={isRequestingExport || exportStatus?.status === 'pending'}>
                {isRequestingExport ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Requesting...
                  </>
                ) : (
                  'Request Export'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => {
        setShowDeleteDialog(open);
        if (!open) setDeleteConfirmation('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Account
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete your account
              and remove all associated data from our servers.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4 text-sm space-y-2">
              <p className="font-medium text-destructive">The following will be deleted:</p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                <li>Your account and profile</li>
                <li>All projects you own</li>
                <li>All secrets in your projects</li>
                <li>Team memberships</li>
                <li>API tokens</li>
                <li>Audit history</li>
              </ul>
            </div>
            {user?.email && (
              <p className="text-sm text-muted-foreground">
                Deleting account for <strong className="text-foreground">{user.email}</strong>
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="delete-confirmation">
                Type <strong>DELETE</strong> to confirm
              </Label>
              <Input
                id="delete-confirmation"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE"
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={isDeleting || deleteConfirmation !== 'DELETE'}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete My Account'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
